// @aiContributed-MAPFuse-2026-02-27
import _ from 'underscore';
import { useContext, useMemo, useCallback, useEffect, useState } from 'react';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import type { Flight, ServerContextType, UseFlightsReturn, UseFlightsParams, FlightStatus } from './index.d';
import {
  FLIGHT_STATUS,
} from './constants';
import { getAccountFlightAllocations } from './fetch-flights';
import { isFlightMatch, getFlightUrlOverrideStatus } from './utils';
import { createServerContextFlightParser } from './parse-flights-from-context';
import { useFlightInstrumentation } from './use-flight-instrumentation';

const getFlightsFromServerContext = createServerContextFlightParser('AccountFlightAllocations');

export type UseAccountFlightsParams = UseFlightsParams & {
  /** Optional: When provided, fetches flight allocations via OData instead of reading from ServerContext */
  accountId?: number;
  /** Optional: Required when accountId is provided for OData fetch */
  customerId?: number;
};

const useAccountFlights = ({
  treatmentFlightId,
  controlFlightId,
  treatmentFlightName,
  controlFlightName,
  autoLog = true,
  accountId,
  customerId,
}: UseAccountFlightsParams): UseFlightsReturn => {
  const [shouldLog, setShouldLog] = useState(autoLog);
  const serverContext = useContext<ServerContextType>(ServerContext);

  const [experimentFlight, setExperimentFlight] = useState<Flight | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  const { flightActivity, nonFlightActivity, allFlightActivity } = useFlightInstrumentation('AccountFlights');

  const getFlightsPromise = useMemo(() => {
    const serverContextAccountId = serverContext?.CurrentAccount?.Id;
    const serverContextCustomerId = serverContext?.CurrentAccount?.AdvertiserCustomerID;
    const isEmptyAllocations = !serverContext?.AccountFlightAllocations || serverContext?.AccountFlightAllocations === '[]';

    // Determine which IDs to use (prefer provided IDs, fall back to server context)
    const resolvedAccountId = accountId ?? serverContextAccountId;
    const resolvedCustomerId = customerId ?? serverContextCustomerId;

    let shouldGetFromServerContext = true;
    if (accountId && customerId) {
      // Use ServerContext if IDs match and allocations are non-empty
      if (!(accountId === serverContextAccountId && customerId === serverContextCustomerId && !isEmptyAllocations)) {
        shouldGetFromServerContext = false;
      }
    } else if (isEmptyAllocations && resolvedAccountId && resolvedCustomerId) {
      // ServerContext allocations are empty but we have valid IDs to fetch via OData
      shouldGetFromServerContext = false;
    }

    if (shouldGetFromServerContext) {
      return Promise.resolve(getFlightsFromServerContext({ serverContext, allFlightActivity }) ?? []);
    }

    return getAccountFlightAllocations({
      aid: resolvedAccountId!,
      cid: resolvedCustomerId!,
      campaignApiBase: serverContext?.ServicesEndpoints?.CampaignAPI,
      oDataToken: serverContext?.Token,
      activity: allFlightActivity,
    });
  }, [accountId, customerId, serverContext, allFlightActivity]);

  useEffect(() => {
    getFlightsPromise
      .then((flights: Flight[]) => {
        const flight = _.find(flights, (f: Flight): boolean =>
          isFlightMatch(f, treatmentFlightId, treatmentFlightName) ||
          isFlightMatch(f, controlFlightId, controlFlightName));
        setExperimentFlight(flight);
        setIsLoading(false);
      })
      .catch((error) => {
        allFlightActivity.trace(JSON.stringify({ error: error?.message }));
        setIsLoading(false);
      });
  }, [allFlightActivity, controlFlightId, treatmentFlightId, treatmentFlightName, controlFlightName, getFlightsPromise]);

  const log = useCallback(() => {
    if (experimentFlight) {
      flightActivity.trace(JSON.stringify(experimentFlight));
    } else {
      nonFlightActivity.trace(JSON.stringify({ treatmentFlightId, controlFlightId }));
    }
  }, [flightActivity, nonFlightActivity, treatmentFlightId, controlFlightId, experimentFlight]);

  const urlOverride = useMemo(
    () => getFlightUrlOverrideStatus(treatmentFlightId, treatmentFlightName),
    [treatmentFlightId, treatmentFlightName]
  );

  const flightStatus = useMemo((): FlightStatus => {
    if (isLoading) {
      return FLIGHT_STATUS.LOADING;
    }

    // support flight url override (e.g. ?flights=123,myFlightName)
    if (urlOverride !== null) {
      return urlOverride;
    }

    if (isFlightMatch(experimentFlight, treatmentFlightId, treatmentFlightName)) {
      return FLIGHT_STATUS.IN_TREATMENT;
    } else if (isFlightMatch(experimentFlight, controlFlightId, controlFlightName)) {
      return FLIGHT_STATUS.IN_CONTROL;
    }

    return FLIGHT_STATUS.NOT_IN_FLIGHT;
  }, [isLoading, experimentFlight, urlOverride, treatmentFlightId, controlFlightId, treatmentFlightName, controlFlightName]);

  const logFlight = useCallback((directlyLog = false) => {
    if (directlyLog) {
      log();
    } else {
      setShouldLog(true);
    }
  }, [log]);

  useEffect(() => {
    if (shouldLog && urlOverride === null && flightStatus !== FLIGHT_STATUS.LOADING) {
      log();
    }
  }, [shouldLog, urlOverride, flightStatus, log]);

  return {
    flightStatus,
    cohortIndex: experimentFlight?.CohortIndex ?? undefined,
    rampAllocationId: experimentFlight?.RampAllocationId ?? undefined,
    ...(autoLog ? {} : { logFlight }),
  };
};

export { useAccountFlights, getFlightsFromServerContext };
